<template>
  <div>
    <CatalogNavigation v-if="!isShowMap" />
    <div
      v-show="isShowCategoriesMenu"
      class="is-shadow"
    />
    <div
      class="mx-auto w-full relative"
      :class="{
        'mb-20 max-w-screen-xl content': !isShowMap,
        'flex flex-grow': isShowMap
      }"
    >
      <Catalog in-root />
    </div>
  </div>
</template>

<script setup>
import { useLoadingIndicator } from '#app/composables/loading-indicator'
import Catalog from '@/components/catalog/index.vue'
import { computed, onActivated, onDeactivated, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import metatags from '~/utils/metatags'

definePageMeta({
  layout: 'catalog',
})

// name используется в include KeepAlive-кеша (layouts/catalog.vue)
defineOptions({
  name: 'CityCatalogIndexPage',
  middleware: 'redirect301',
})

const route = useRoute()
const { $tracking } = useNuxtApp()
const $localePath = useLocalePath()

const search = useSearch()
const searchForm = useSearchForm()
const { getCityBySlug } = useCities()

const isShowMap = searchForm.isShowMap
const isShowCategoriesMenu = computed(() => useGlobal().isShowCategoriesMenu.value || false)

const pageInfo = search.pageInfo
const pageRef = searchForm.page
const totalCount = search.totalCount
const viewSearchPageEventSent = search.viewSearchPageEventSent
const viewSearchPage = search.viewSearchPage

const { shouldSkipCatalogFetch } = useCatalogKeepaliveGuard()

const { error, pending } = await useAsyncData('catalog-init', async () => {
  if (shouldSkipCatalogFetch()) {
    return null
  }

  const params = route.params
  const query = route.query

  if (params.slug && params.slug.length > 0) {
    return null
  }

  const city = getCityBySlug(params.city)
  if (!city) {
    throw ({ statusCode: 404, message: 'Город не найден' })
  }

  searchForm.setParams({})

  const q = query.q || ''
  searchForm.setQuery(q)

  const page = query.page || 1
  searchForm.setPage(page)

  searchForm.setCity(city)
  const cityIdCookie = useAppCookie('rm_city_id')
  cityIdCookie.value = city.id

  searchForm.selectCategory({})

  const path = $localePath(`/${params.city}/catalog`)
  searchForm.setPath(path)
  searchForm.setFullPath(path)

  const positionLatCookie = useAppCookie('position_lat')
  const positionLngCookie = useAppCookie('position_lng')
  const lat = Number(query.lat) || positionLatCookie.value || city.location.lat
  const lng = Number(query.lng) || positionLngCookie.value || city.location.lng
  positionLatCookie.value = lat
  positionLngCookie.value = lng
  useUser().setLocation({ lat, lng })

  const topLeftLat = query.topLeftLat
  const topLeftLng = query.topLeftLng
  const bottomRightLat = query.bottomRightLat
  const bottomRightLng = query.bottomRightLng

  if (topLeftLat && topLeftLng && bottomRightLat && bottomRightLng) {
    searchForm.setGeoBoundingBox({
      topLeftLat: Number(topLeftLat),
      topLeftLng: Number(topLeftLng),
      bottomRightLat: Number(bottomRightLat),
      bottomRightLng: Number(bottomRightLng),
    })
    searchForm.setIsShowMap(true)
  } else {
    searchForm.setGeoBoundingBox({})
    useGlobal().setFromBack(false)
    searchForm.setIsShowMap(false)
  }

  const sortBy = route.query.sort_by || 'RELEVANCE'
  const direction = route.query.direction || 'DESC'
  searchForm.setSortBySlug(sortBy.toUpperCase())
  searchForm.setSortBySortDirection(direction.toUpperCase())

  const shouldSkipFetchLots = search.loadMorePendingRefetch.value
  if (shouldSkipFetchLots) {
    search.setLoadMorePendingRefetch(false)
  } else {
    await search.fetchLots({
      error: () => {},
      query,
      inRoot: true,
    })
  }

  for (const key of Object.keys(query)) {
    searchForm.changeParamsRaw({ key, value: query[key] })
  }
  return {}
}, {
  watch: [() => route.query, () => route.params],
})

if (error.value) {
  throw createError({
    statusCode: 404,
    message: error.value.message,
  })
}

watch(pending, newPending => {
  const indicator = useLoadingIndicator()
  if (newPending) {
    indicator.start({ force: true })
  } else {
    indicator.finish({ force: true })
  }
})



const { t } = useI18n()
const title = computed(() => {

  if (pageInfo.value?.currentPage > 1) {
    return `${t('common.rentOn')} Polka.rent страница ${pageInfo.value.currentPage}`
  }
  return `${t('common.rentOn')} Polka.rent`
})

// Head/meta
const description = computed(() => {
  let content = 'Полный каталог товаров в аренду на Polka.rent! Откройте для себя тысячи предложений — от электроники до детских товаров. Экономьте время и деньги, арендуя с Polka.rent!'
  if (route.fullPath in metatags) {
    content = metatags[route.fullPath].description
  }
  return { hid: 'description', name: 'description', content }
})


useHead({
  title: title.value,
  meta: [description.value],
})


// Tracking. navigation type (NEW/BACK) добавляет tracking plugin из useGlobal().
const sendViewSearchPage = () => {
  $tracking.logEventAmplitude('view search page', {
    query: route.query.q,
    'page name': 'Поиск',
    'page type': 'SEARCH',
    'num of products': totalCount.value,
    url: window.location.href,
  })
}

const sendViewCategoryPage = () => {
  $tracking.logEventAmplitude('view category page', {
    'page name': 'Каталог',
    'page type': 'CATEGORY',
    'batch number': Number(pageRef.value),
    'product list': '',
    'category depth': '',
    'category id': '',
    'category name': '',
    'sort type': '',
    query: '',
    'num of products': totalCount.value,
  })
}

onMounted(() => {
  if (route.query.q) {
    // Не шлём со страницы, если событие отправит store после fetchLots (поиск со страницы категории)
    if (!viewSearchPageEventSent.value && !viewSearchPage.value) {
      sendViewSearchPage()
    }
  } else {
    sendViewCategoryPage()
  }
})

// Повторный показ keepalive-страницы («Назад» с лота): onMounted не срабатывает,
// шлём view-событие из onActivated. Только после onDeactivated — на первичном
// маунте (с async setup внутри Suspense) onActivated ненадёжен и не нужен.
let wasDeactivated = false
onDeactivated(() => {
  wasDeactivated = true
})
onActivated(() => {
  if (!wasDeactivated) {
    return
  }
  wasDeactivated = false

  if (route.query.q) {
    sendViewSearchPage()
  } else {
    sendViewCategoryPage()
  }
})
</script>

<style lang="sass" scoped>
h1
  font-size: 62px
  text-align: center
  @apply text-gray-900 mb-20
  .b
    @apply font-bold
</style>
